import React from 'react';
import { alpha, Box, Button, Grid, LinearProgress, Paper, Typography } from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { AccountData } from '../../../api';
import { getOrderPage } from '../../../utils/AccountUtils';
import { DEFAULT_ORDER_PAGE_COLOR, mb, softFill } from '../../../common/theme/mbTokens';
import { useCalendar } from '../../CalendarContext/CalendarContext';
import { getEmployeeNames } from '../../CalendarUtils';
import { useCourse } from '../../swr/useCourse';
import {
  BODY_WEIGHT,
  CARD_PADDING,
  CARD_PADDING_INLINE,
  CARD_RADIUS,
  FONT_SIZE,
  ICON_SIZE,
  MUTED_TEXT,
} from '../agendaStyles';
import { formatSeconds } from '../utils/areasFormat';
import { AgendaSession } from '../utils/agendaSessions';
import { RoomChip } from './RoomChip';

type SessionCardProps = {
  session: AgendaSession;
  title: string;
  account: AccountData;
  timeFormat: string;
  isHighlighted: boolean;
  onOpen: () => void;
};

const PROGRESS_WIDTH = 120;

/**
 * One group event or course session of the day. Tapping the card or its button opens the event dialog,
 * where the roster and the actions live.
 */
export const SessionCard = ({ session, title, account, timeFormat, isHighlighted, onOpen }: SessionCardProps) => {
  const { t } = useTranslation();
  const { users } = useCalendar();
  const { event, used } = session;
  const { courseData } = useCourse(event.course_id);

  const startSeconds = event.time ?? 0;
  const timeRange = `${formatSeconds(startSeconds, timeFormat)}–${formatSeconds(
    startSeconds + event.duration,
    timeFormat,
  )}`;
  const sessionIndex = courseData?.sessions?.findIndex((s) => s.date === event.date && s.time === event.time);
  const sessionNumber = sessionIndex !== undefined && sessionIndex >= 0 ? sessionIndex + 1 : null;
  const workshopColor = getOrderPage(account, event.order_page_id)?.color || DEFAULT_ORDER_PAGE_COLOR;
  const staffNames = getEmployeeNames(event.employee_ids, users);
  const progress = event.tickets > 0 ? Math.min(100, (used / event.tickets) * 100) : 0;

  const timeElement = (
    <Typography
      dir="ltr"
      sx={{
        fontSize: FONT_SIZE.time,
        fontWeight: 400,
        color: MUTED_TEXT,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        fontVariantNumeric: 'tabular-nums',
      }}>
      {timeRange}
    </Typography>
  );
  const titleElement = (
    <Grid container alignItems="center" gap={0.75} wrap="nowrap" sx={{ flex: '1 1 160px', minWidth: 0 }}>
      <GroupsOutlinedIcon sx={{ fontSize: ICON_SIZE, color: MUTED_TEXT, flexShrink: 0 }} />
      <Typography
        sx={{
          fontSize: FONT_SIZE.title,
          fontWeight: 700,
          lineHeight: 1.2,
          minWidth: 0,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}>
        {title}
      </Typography>
    </Grid>
  );
  const courseChip =
    event.course_id && sessionNumber && courseData ? (
      <Box
        sx={{
          backgroundColor: alpha(mb.ink, 0.08),
          borderRadius: '4px',
          paddingInline: 1,
          paddingBlock: 0.25,
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}>
        {t('calendar.course_session.session_label', { current: sessionNumber, total: courseData.sessions?.length })}
      </Box>
    ) : null;
  const hiddenChip = event.hidden_from_customers ? (
    <Grid
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        paddingInline: 1,
        paddingBlock: 0.25,
        borderRadius: '4px',
        bgcolor: 'mb.claySoft',
        color: 'mb.clayInk',
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}>
      <VisibilityOffIcon sx={{ fontSize: 14 }} />
      {t('calendar.areas_view.hidden_from_customers')}
    </Grid>
  ) : null;

  return (
    <Paper
      variant="outlined"
      data-agenda-item-id={event.id}
      sx={{
        borderRadius: CARD_RADIUS,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: mb.surface,
        ...(used === 0 && { opacity: 0.6 }),
        ...(isHighlighted && {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '2px',
          transition: 'outline-color 300ms',
        }),
      }}>
      <Grid container wrap="nowrap" sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' } }}>
        <Grid
          container
          direction="column"
          gap={1.25}
          wrap="nowrap"
          role="button"
          tabIndex={0}
          onClick={onOpen}
          onKeyDown={(keyEvent: React.KeyboardEvent) => {
            if (keyEvent.target !== keyEvent.currentTarget) {
              return;
            }
            if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
              keyEvent.preventDefault();
              onOpen();
            }
          }}
          sx={{ flex: 1, minWidth: 0, cursor: 'pointer', padding: CARD_PADDING }}>
          <Grid container alignItems="center" columnGap={1.5} rowGap={0.5} wrap="wrap">
            {timeElement}
            {titleElement}
            {courseChip}
            {hiddenChip}
          </Grid>

          <Grid container alignItems="center" gap={2} rowGap={1} wrap="wrap">
            <RoomChip
              label={t(event.course_id ? 'editOrderPage.orderPageTypeCourse' : 'calendar.joiningWorkshop')}
              dot={workshopColor}
              soft={softFill(workshopColor)}
            />

            {used === 0 ? (
              <Typography sx={{ fontSize: FONT_SIZE.secondary, fontWeight: BODY_WEIGHT, color: MUTED_TEXT }}>
                {t('calendar.areas_view.no_participants')}
              </Typography>
            ) : (
              <Grid container alignItems="center" gap={1} wrap="nowrap" sx={{ width: 'auto', flexShrink: 0 }}>
                <Typography sx={{ fontSize: FONT_SIZE.secondary, fontWeight: BODY_WEIGHT, whiteSpace: 'nowrap' }}>
                  <span dir="ltr">
                    {used}/{event.tickets}
                  </span>{' '}
                  {t('calendar.event_popper.participants')}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  color="primary"
                  sx={{ width: PROGRESS_WIDTH, height: 6, borderRadius: '3px' }}
                />
              </Grid>
            )}

            {staffNames.length > 0 && (
              <Grid container alignItems="center" gap={0.5} wrap="wrap" sx={{ width: 'auto', minWidth: 0 }}>
                {staffNames.map((name, index) => (
                  <Grid
                    key={`${index}-${name}`}
                    border="1px solid"
                    borderColor="mb.control"
                    px={1}
                    borderRadius="4px"
                    sx={{ fontSize: FONT_SIZE.secondary, fontWeight: BODY_WEIGHT }}>
                    {name}
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid
          sx={{
            flexShrink: 0,
            paddingInline: { xs: CARD_PADDING_INLINE.xs, md: 0 },
            paddingInlineEnd: { md: 2.5 },
            paddingBottom: { xs: '12px', md: 0 },
          }}>
          <Button
            variant="outlined"
            startIcon={<ListAltIcon />}
            onClick={(clickEvent) => {
              clickEvent.stopPropagation();
              onOpen();
            }}
            sx={{
              minHeight: 44,
              fontSize: FONT_SIZE.text,
              paddingInline: 2,
              whiteSpace: 'nowrap',
              width: { xs: '100%', md: 'auto' },
            }}>
            {t('calendar.event_popper.participants')}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};
